import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  collection, addDoc, updateDoc, doc,
  onSnapshot, query, orderBy, where, serverTimestamp
} from 'firebase/firestore';
import { signOut, onAuthStateChanged } from 'firebase/auth';
import { db, auth } from './firebase';
import { getStatusColor, getPriorityColor, getUserNameByEmail, formatDateTime } from './utils/helpers';
import { notifyOnComment, notifyDefectCreator } from './services/notificationService';
import {
  exportToExcel,
  exportToCSV,
  exportToMarkdown,
  exportToPDF,
  exportToText,
  formatDataForExport
} from './services/exportService';
import { convertFeatureToDefect } from './services/conversionService';
import { nextFeatureNumber, backfillFeatureNumbers } from './services/applicationCounterService';
import { compressImage, validateImageFile } from './utils/imageCompression';
import { buildPermalink, copyToClipboard, pinEntityToDesktop } from './utils/shortcutHelpers';
import LoadingSpinner from './components/LoadingSpinner';
import Modal from './components/Modal';
import FeatureRequestBoard from './FeatureRequestBoard';
import FeatureRequestsAdminView from './FeatureRequestsAdminView';
import AdminPanel from './AdminPanel';
import ApplicationManager from './ApplicationManager';
import Notifications from './Notifications';
import './FeatureRequestsApp.css';

const STATUSES = ['Submitted', 'Under Review', 'Planned', 'In Progress', 'Testing', 'Completed', 'Rejected'];
const PRIORITIES = ['Critical', 'High', 'Medium', 'Low'];

function FeatureRequestsApp() {
  const navigate = useNavigate();
  const { requestId } = useParams();  // Get request ID from URL
  
  const [user, setUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [requests, setRequests] = useState([]);
  const [users, setUsers] = useState([]);
  const [applications, setApplications] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingRequest, setEditingRequest] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [filterStatus, setFilterStatus] = useState('All');
  const [filterPriority, setFilterPriority] = useState('All');
  const [filterApplication, setFilterApplication] = useState([]);
  const [sortBy, setSortBy] = useState('createdAt');
  const [sortOrder, setSortOrder] = useState('desc');
  const [imagePreview, setImagePreview] = useState(null);
  const [commentInputs, setCommentInputs] = useState({});
  const [editingComment, setEditingComment] = useState(null);
  const [editCommentText, setEditCommentText] = useState('');
  const [showAdminView, setShowAdminView] = useState(false);
  const [showAdminPanel, setShowAdminPanel] = useState(false);
  const [showAppManager, setShowAppManager] = useState(false);
  const [showExportDropdown, setShowExportDropdown] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [hiddenLanes, setHiddenLanes] = useState(new Set());
  const [viewOnlyRequest, setViewOnlyRequest] = useState(null);

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    applicationName: '',
    mockupImage: null,
    mockupImageUrl: '',
    priority: 'Medium',
    status: 'Submitted',
    assignedToDev: '',
    assignedToTest: '',
    testStep: '',
    comments: ''
  });

  const role = userProfile?.role || null;
  const isAdmin = role === 'Admin';
  const isTester = role === 'Tester/QA';

  // Auth listener
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (currentUser) => {
      setUser(currentUser);
      if (currentUser) {
        const profileUnsub = onSnapshot(doc(db, 'users', currentUser.uid), (snap) => {
          if (snap.exists()) {
            setUserProfile({ id: snap.id, ...snap.data() });
          }
          setAuthLoading(false);
        });
        return () => profileUnsub();
      } else {
        setAuthLoading(false);
      }
    });
    return () => unsub();
  }, []);

  // Set page title based on URL
  useEffect(() => {
    if (requestId) {
      // Permalink - will be updated with actual title when request loads
      document.title = 'Loading Feature Request... - Feature Requests';
    } else {
      document.title = 'Feature Requests - Wixon & Cross Desk Suite';
    }
  }, [requestId]);

  // Update page title when viewing a specific feature request
  useEffect(() => {
    if (viewOnlyRequest) {
      document.title = `${viewOnlyRequest.title} - Feature Requests`;
    } else if (!requestId) {
      // Only reset to default if not on a permalink URL
      document.title = 'Feature Requests - Wixon & Cross Desk Suite';
    }
  }, [viewOnlyRequest, requestId]);

  // Feature Requests listener
  useEffect(() => {
    if (!user) return;

    const q = query(collection(db, 'featureRequests'), orderBy('createdAt', 'desc'));
    const unsub = onSnapshot(q, (snap) => {
      setRequests(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    }, (err) => {
      console.error('Error fetching feature requests:', err);
    });

    return () => unsub();
  }, [user]);

  // Users listener
  useEffect(() => {
    if (!user) return;
    
    const q = query(collection(db, 'users'), orderBy('email'));
    const unsub = onSnapshot(q, snap => {
      setUsers(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    });
    return () => unsub();
  }, [user]);

  // Applications listener
  useEffect(() => {
    if (!user) return;
    
    const q = query(collection(db, 'applications'), orderBy('name'));
    const unsub = onSnapshot(q, snap => {
      setApplications(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    });
    return () => unsub();
  }, [user]);

  // One-time backfill of sequential feature-request numbers (admin only).
  // Idempotent — gated by `featuresBackfilled` flag in the counter doc.
  useEffect(() => {
    if (!user || !isAdmin) return;
    backfillFeatureNumbers().catch(err => {
      console.error('Feature number backfill failed:', err);
    });
  }, [user, isAdmin]);

  // Notifications listener
  useEffect(() => {
    if (!user) return;
    const q = query(
      collection(db, 'notifications'),
      where('userId', '==', user.uid),
      where('dismissed', '==', false),
      orderBy('createdAt', 'desc')
    );
    const unsub = onSnapshot(q, snap => {
      setNotifications(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    });
    return () => unsub();
  }, [user]);

  // Handle permalink - open request from URL
  useEffect(() => {
    if (requestId && requests.length > 0) {
      const request = requests.find(r => r.id === requestId);
      if (request) {
        setViewOnlyRequest(request);
      }
    }
  }, [requestId, requests]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleCloseViewOnly = () => {
    setViewOnlyRequest(null);
    // If we're on a permalink URL, navigate back to base feature-requests
    if (requestId) {
      navigate('/feature-requests');
    }
  };

  const handleSignOut = async () => {
    try {
      await signOut(auth);
      navigate('/dashboard');
    } catch (e) {
      alert('Error signing out.');
    }
  };

  const handleImageChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    // Validate image
    const validation = validateImageFile(file);
    if (!validation.valid) {
      alert(validation.error);
      return;
    }
    
    // Compress image
    const compressedFile = await compressImage(file);
    
    // Create preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setImagePreview(reader.result);
      setFormData(prev => ({ ...prev, mockupImage: compressedFile }));
    };
    reader.readAsDataURL(compressedFile);
  };

  const uploadImage = async (file) => {
    if (!file) return null;
    
    // Convert to base64 instead of uploading to Firebase Storage
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setUploading(true);

    try {
      let mockupUrl = editingRequest?.mockupImageUrl || '';
      if (formData.mockupImage) {
        mockupUrl = await uploadImage(formData.mockupImage);
      }

      const requestData = {
        title: formData.title,
        description: formData.description,
        applicationName: formData.applicationName,
        mockupImageUrl: mockupUrl,
        priority: formData.priority,
        status: formData.status,
        assignedToDev: formData.assignedToDev || '',
        assignedToTest: formData.assignedToTest || '',
        testStep: formData.testStep || '',
        votes: editingRequest?.votes || {},
        voteCount: editingRequest?.voteCount || 0
      };

      if (editingRequest) {
        await updateDoc(doc(db, 'featureRequests', editingRequest.id), {
          ...requestData,
          updatedAt: serverTimestamp(),
          updatedBy: user.email
        });
      } else {
        // Reserve the next sequential number for this application before creating.
        let displayNumber = null;
        try {
          displayNumber = await nextFeatureNumber(formData.applicationName);
        } catch (err) {
          console.error('Could not reserve feature number:', err);
        }

        await addDoc(collection(db, 'featureRequests'), {
          ...requestData,
          ...(displayNumber !== null ? { displayNumber } : {}),
          comments: [],
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
          createdBy: user.uid,
          createdByEmail: user.email
        });
      }

      resetForm();
    } catch (err) {
      console.error('Error saving feature request:', err);
      alert('Error saving feature request.');
    } finally {
      setUploading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      applicationName: '',
      mockupImage: null,
      mockupImageUrl: '',
      priority: 'Medium',
      status: 'Submitted',
      assignedToDev: '',
      assignedToTest: '',
      testStep: '',
      comments: ''
    });
    setImagePreview(null);
    setEditingRequest(null);
    setIsFormOpen(false);
  };

  const handleEdit = (request) => {
    setEditingRequest(request);
    setFormData({
      title: request.title,
      description: request.description,
      applicationName: request.applicationName || '',
      mockupImage: null,
      mockupImageUrl: request.mockupImageUrl || '',
      priority: request.priority,
      status: request.status,
      assignedToDev: request.assignedToDev || '',
      assignedToTest: request.assignedToTest || '',
      testStep: request.testStep || '',
      comments: ''
    });
    setImagePreview(request.mockupImageUrl || null);
    setIsFormOpen(true);
  };

  const handleVote = async (request, voteType) => {
    if (!user) return;

    const currentVotes = request.votes || {};
    const currentUserVote = currentVotes[user.uid];

    let newVotes = { ...currentVotes };
    let voteChange = 0;

    if (currentUserVote === voteType) {
      // Remove vote
      delete newVotes[user.uid];
      voteChange = voteType === 'up' ? -1 : 1;
    } else {
      // Add or change vote
      if (currentUserVote) {
        voteChange = voteType === 'up' ? 2 : -2;
      } else {
        voteChange = voteType === 'up' ? 1 : -1;
      }
      newVotes[user.uid] = voteType;
    }

    try {
      await updateDoc(doc(db, 'featureRequests', request.id), {
        votes: newVotes,
        voteCount: (request.voteCount || 0) + voteChange,
        updatedAt: serverTimestamp()
      });
    } catch (err) {
      console.error('Error voting:', err);
      alert('Error recording vote.');
    }
  };

  const handleAddComment = async (request, text) => {
    if (!text.trim()) return;

    const newComment = {
      text: text.trim(),
      createdBy: user.email,
      createdAt: new Date().toISOString()
    };

    try {
      await updateDoc(doc(db, 'featureRequests', request.id), {
        comments: [...request.comments, newComment],
        updatedAt: serverTimestamp()
      });

      setCommentInputs(prev => ({ ...prev, [request.id]: '' }));

      // Notify about comment
      await notifyOnComment(request, users, user.email, user.uid);
    } catch (err) {
      alert('Error adding comment.');
    }
  };

  const handleEditComment = (requestId, commentIndex, comment) => {
    setEditingComment(`${requestId}-${commentIndex}`);
    setEditCommentText(comment.text);
  };

  const handleSaveCommentEdit = async (requestId, commentIndex) => {
    if (!editCommentText.trim()) {
      alert('Comment cannot be empty');
      return;
    }

    const request = requests.find(r => r.id === requestId);
    if (!request) return;

    const updatedComments = [...request.comments];
    updatedComments[commentIndex] = {
      ...updatedComments[commentIndex],
      text: editCommentText.trim(),
      editedAt: new Date().toISOString(),
      editedBy: user.email
    };

    try {
      await updateDoc(doc(db, 'featureRequests', requestId), {
        comments: updatedComments,
        updatedAt: serverTimestamp()
      });

      setEditingComment(null);
      setEditCommentText('');
    } catch (err) {
      alert('Error updating comment.');
    }
  };

  const handleCancelCommentEdit = () => {
    setEditingComment(null);
    setEditCommentText('');
  };

  // Export functionality
  const handleExport = (format) => {
    // Filter out requests in hidden swimlanes
    const visibleRequests = filteredRequests.filter(r => !hiddenLanes.has(r.status));
    const data = formatDataForExport(visibleRequests, 'feature');
    const filename = `feature-requests-${new Date().toISOString().split('T')[0]}`;
    const title = 'Feature Requests Export';
    
    switch (format) {
      case 'excel':
        exportToExcel(data, filename);
        break;
      case 'csv':
        exportToCSV(data, filename);
        break;
      case 'markdown':
        exportToMarkdown(data, filename, title);
        break;
      case 'pdf':
        exportToPDF(data, filename, title);
        break;
      case 'text':
        exportToText(data, filename, title);
        break;
      default:
        break;
    }
  };

  // Convert to defect
  const handleConvertToDefect = async (request) => {
    if (!window.confirm(`Convert "${request.title}" to a Defect? This will delete the feature request.`)) {
      return;
    }
    
    try {
      await convertFeatureToDefect(request);
      alert('Successfully converted to Defect!');
    } catch (error) {
      console.error('Conversion error:', error);
      alert('Error converting feature: ' + error.message);
    }
  };

  // Filter requests
  const filteredRequests = requests.filter(req => {
    if (filterStatus !== 'All' && req.status !== filterStatus) return false;
    if (filterPriority !== 'All' && req.priority !== filterPriority) return false;
    if (filterApplication.length > 0 && !filterApplication.includes(req.applicationName)) return false;
    return true;
  }).sort((a, b) => {
    let aVal, bVal;
    
    if (sortBy === 'createdAt' || sortBy === 'updatedAt') {
      aVal = a[sortBy]?.toDate?.() || new Date(0);
      bVal = b[sortBy]?.toDate?.() || new Date(0);
    } else if (sortBy === 'priority') {
      const priorityOrder = { 'Critical': 4, 'High': 3, 'Medium': 2, 'Low': 1 };
      aVal = priorityOrder[a.priority] || 0;
      bVal = priorityOrder[b.priority] || 0;
    } else if (sortBy === 'votes') {
      aVal = (a.upvotes?.length || 0) - (a.downvotes?.length || 0);
      bVal = (b.upvotes?.length || 0) - (b.downvotes?.length || 0);
    } else if (sortBy === 'applicationName') {
      aVal = (a.applicationName || '').toLowerCase();
      bVal = (b.applicationName || '').toLowerCase();
    } else if (sortBy === 'displayNumber') {
      aVal = typeof a.displayNumber === 'number' ? a.displayNumber : Number.POSITIVE_INFINITY;
      bVal = typeof b.displayNumber === 'number' ? b.displayNumber : Number.POSITIVE_INFINITY;
    } else {
      aVal = a[sortBy] || '';
      bVal = b[sortBy] || '';
    }
    
    if (sortOrder === 'asc') {
      return aVal > bVal ? 1 : aVal < bVal ? -1 : 0;
    } else {
      return aVal < bVal ? 1 : aVal > bVal ? -1 : 0;
    }
  });

  // Stats
  const stats = [
    { label: 'Total Requests', value: requests.length },
    { label: 'Submitted', value: requests.filter(r => r.status === 'Submitted').length },
    { label: 'In Progress', value: requests.filter(r => r.status === 'In Progress').length },
    { label: 'Completed', value: requests.filter(r => r.status === 'Completed').length }
  ];

  if (authLoading) return <LoadingSpinner message="Loading..." />;
  if (!user || !userProfile) return <LoadingSpinner message="Setting up..." />;

  if (showAdminView && isAdmin) return (
    <FeatureRequestsAdminView 
      onNavigate={(section) => {
        if (section === 'users') {
          setShowAdminView(false);
          setShowAdminPanel(true);
        } else if (section === 'apps') {
          setShowAdminView(false);
          setShowAppManager(true);
        }
      }}
      onBack={() => setShowAdminView(false)}
    />
  );

  if (showAdminPanel && isAdmin) return (
    <AdminPanel user={user} onBack={() => setShowAdminPanel(false)} />
  );

  if (showAppManager && isAdmin) return (
    <ApplicationManager user={user} onBack={() => setShowAppManager(false)} />
  );

  if (showNotifications) return (
    <Notifications user={user} onBack={() => setShowNotifications(false)} />
  );

  const unreadCount = notifications.filter(n => !n.read).length;

  return (
    <div className="feature-requests-app">
      <header className="header">
        <div className="header-content">
          <div>
            <div className="breadcrumb-nav-app">
              <button className="breadcrumb-item" onClick={() => navigate('/desktop')}>
                🖥️ Desktop
              </button>
              <span className="breadcrumb-separator">›</span>
              <button className="breadcrumb-item" onClick={() => navigate('/dashboard')}>
                📦 Dashboard
              </button>
              <span className="breadcrumb-separator">›</span>
              <span className="breadcrumb-item breadcrumb-current">💡 Feature Requests</span>
            </div>
          </div>
          <div className="header-user">
            <button 
              className="btn btn-notification" 
              onClick={() => setShowNotifications(true)}
            >
              🔔 Notifications
              {unreadCount > 0 && <span className="notification-badge">{unreadCount}</span>}
            </button>
            <div className="user-info-section">
              <span className="user-email">{user.email}</span>
              <span className="user-role-label">Role: <span className="role-badge role-badge--header">{role}</span></span>
            </div>
            {isAdmin && (
              <button className="btn btn-warning btn-small" onClick={() => navigate('/admin')}>
                ⚙️ Admin
              </button>
            )}
            <button className="btn btn-secondary btn-small" onClick={handleSignOut}>
              Sign Out
            </button>
          </div>
        </div>
      </header>

      <main className="main-content">
        {/* Stats */}
        <div className="stats">
          {stats.map((s, i) => (
            <div key={i} className="stat-card">
              <div className="stat-value">{s.value}</div>
              <div className="stat-label">{s.label}</div>
            </div>
          ))}
        </div>

        {/* Toolbar */}
        <div className="toolbar">
          <button className="btn btn-primary" onClick={() => setIsFormOpen(true)}>
            + New Request
          </button>
          <div className="export-dropdown">
            <button 
              className="btn btn-secondary"
              onClick={() => setShowExportDropdown(!showExportDropdown)}
            >
              📊 Export ▼
            </button>
            {showExportDropdown && (
              <div className="dropdown-menu">
                <button onClick={() => { handleExport('excel'); setShowExportDropdown(false); }}>Excel (.xlsx)</button>
                <button onClick={() => { handleExport('csv'); setShowExportDropdown(false); }}>CSV (.csv)</button>
                <button onClick={() => { handleExport('markdown'); setShowExportDropdown(false); }}>Markdown (.md)</button>
                <button onClick={() => { handleExport('pdf'); setShowExportDropdown(false); }}>PDF (.pdf)</button>
                <button onClick={() => { handleExport('text'); setShowExportDropdown(false); }}>Text (.txt)</button>
              </div>
            )}
          </div>
        </div>

        {/* Filters */}
        <div className="toolbar">
          <div className="filters">
            <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} className="filter-select">
              <option value="All">All Status</option>
              {STATUSES.map(s => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
            <select value={filterPriority} onChange={e => setFilterPriority(e.target.value)} className="filter-select">
              <option value="All">All Priorities</option>
              {PRIORITIES.map(p => (
                <option key={p} value={p}>{p}</option>
              ))}
            </select>
            <div className="filter-multiselect">
              <label>Applications:</label>
              <div className="multiselect-dropdown">
                <button 
                  className="multiselect-button"
                  onClick={() => {
                    const dropdown = document.getElementById('fr-app-filter-dropdown');
                    dropdown.style.display = dropdown.style.display === 'block' ? 'none' : 'block';
                  }}
                >
                  {filterApplication.length === 0 
                    ? 'All Applications' 
                    : `${filterApplication.length} selected`}
                  <span className="dropdown-arrow">▼</span>
                </button>
                <div id="fr-app-filter-dropdown" className="multiselect-dropdown-content" style={{display: 'none'}}>
                  <div className="multiselect-actions">
                    <button 
                      className="btn-link" 
                      onClick={() => setFilterApplication(applications.map(a => a.name))}
                    >
                      Select All
                    </button>
                    <button 
                      className="btn-link" 
                      onClick={() => setFilterApplication([])}
                    >
                      Clear All
                    </button>
                  </div>
                  <div className="multiselect-options">
                    {applications.map(app => (
                      <label key={app.id} className="checkbox-label">
                        <input
                          type="checkbox"
                          checked={filterApplication.includes(app.name)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setFilterApplication([...filterApplication, app.name]);
                            } else {
                              setFilterApplication(filterApplication.filter(a => a !== app.name));
                            }
                          }}
                        />
                        <span>{app.name}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          {/* Sort Controls */}
          <div className="sort-controls">
            <label>Sort by:</label>
            <select value={sortBy} onChange={e => setSortBy(e.target.value)} className="filter-select">
              <option value="displayNumber">Number (e.g. WP-F.1)</option>
              <option value="createdAt">Created Date</option>
              <option value="updatedAt">Updated Date</option>
              <option value="priority">Priority</option>
              <option value="votes">Net Votes</option>
              <option value="applicationName">Application</option>
              <option value="title">Title</option>
            </select>
            <select value={sortOrder} onChange={e => setSortOrder(e.target.value)} className="filter-select">
              <option value="desc">Descending</option>
              <option value="asc">Ascending</option>
            </select>
          </div>
        </div>

        {/* Board View */}
        <FeatureRequestBoard
          requests={filteredRequests}
          user={user}
          userRole={role}
          users={users}
          onEditRequest={handleEdit}
          onViewRequest={setViewOnlyRequest}
          onVote={handleVote}
          onAddComment={handleAddComment}
          commentInputs={commentInputs}
          setCommentInputs={setCommentInputs}
          editingComment={editingComment}
          editCommentText={editCommentText}
          setEditCommentText={setEditCommentText}
          onEditComment={handleEditComment}
          onSaveCommentEdit={handleSaveCommentEdit}
          onCancelCommentEdit={handleCancelCommentEdit}
          onConvertToDefect={handleConvertToDefect}
          onHiddenLanesChange={setHiddenLanes}
        />
      </main>

      {/* Form Modal */}
      <Modal
        isOpen={isFormOpen}
        onClose={resetForm}
        title={editingRequest ? 'Edit Feature Request' : 'New Feature Request'}
      >
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Title *</label>
            <input
              type="text"
              name="title"
              value={formData.title}
              onChange={handleInputChange}
              required
              placeholder="Brief description of the feature"
            />
          </div>

          <div className="form-group">
            <label>Application *</label>
            <select
              name="applicationName"
              value={formData.applicationName}
              onChange={handleInputChange}
              required
            >
              <option value="">Select Application</option>
              {applications.map(app => (
                <option key={app.id} value={app.name}>{app.name}</option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label>Description *</label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              required
              rows="2"
              placeholder="Detailed description of the feature request"
              style={{ minHeight: '60px' }}
            />
          </div>

          <div className="form-group">
            <label>Test Step/Summary</label>
            <textarea
              name="testStep"
              value={formData.testStep}
              onChange={handleInputChange}
              rows="2"
              placeholder="How to test this feature once implemented"
              style={{ minHeight: '60px' }}
            />
          </div>

          <div className="form-group">
            <label>Mockup/Screenshot (Optional)</label>
            <input
              type="file"
              accept="image/*"
              onChange={handleImageChange}
            />
            {imagePreview && (
              <div className="image-preview-container">
                <img src={imagePreview} alt="Mockup preview" className="image-preview" />
              </div>
            )}
          </div>

          {(isAdmin || isTester) && (
            <>
              <div className="form-group">
                <label>Priority</label>
                <select name="priority" value={formData.priority} onChange={handleInputChange}>
                  {PRIORITIES.map(p => (
                    <option key={p} value={p}>{p}</option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>Status</label>
                <select name="status" value={formData.status} onChange={handleInputChange}>
                  {STATUSES.map(s => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
              </div>

              {(formData.status === 'Planned' || formData.status === 'In Progress' || formData.status === 'Testing') && (
                <div className="form-group">
                  <label>Assign Developer</label>
                  <select 
                    name="assignedToDev" 
                    value={formData.assignedToDev} 
                    onChange={handleInputChange}
                  >
                    <option value="">Unassigned</option>
                    {users.filter(u => u.role === 'Admin' || u.role === 'Tester/QA').map(u => (
                      <option key={u.id} value={u.email}>{u.email}</option>
                    ))}
                  </select>
                </div>
              )}

              {(formData.status === 'In Progress' || formData.status === 'Testing') && (
                <div className="form-group">
                  <label>Assign Tester</label>
                  <select 
                    name="assignedToTest" 
                    value={formData.assignedToTest} 
                    onChange={handleInputChange}
                  >
                    <option value="">Unassigned</option>
                    {users.filter(u => u.role === 'Admin' || u.role === 'Tester/QA').map(u => (
                      <option key={u.id} value={u.email}>{u.email}</option>
                    ))}
                  </select>
                </div>
              )}
            </>
          )}

          <div className="form-actions">
            <button type="button" className="btn btn-secondary" onClick={resetForm}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={uploading}>
              {uploading ? 'Saving...' : (editingRequest ? 'Update Request' : 'Create Request')}
            </button>
          </div>
        </form>
      </Modal>

      {/* View-Only Request Modal */}
      {viewOnlyRequest && (
        <div className="modal-overlay" onClick={handleCloseViewOnly}>
          <div className="modal view-only-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>View Feature Request</h3>
              <div style={{display: 'flex', gap: '0.5rem', alignItems: 'center'}}>
                <button 
                  className="btn btn-secondary btn-small"
                  onClick={async () => {
                    const url = buildPermalink('/feature-requests', viewOnlyRequest.id);
                    const ok = await copyToClipboard(url);
                    alert(ok ? 'Permalink copied to clipboard!' : 'Could not copy link');
                  }}
                  title="Copy link to this feature request"
                >
                  🔗 Copy Link
                </button>
                <button
                  className="btn btn-secondary btn-small"
                  onClick={async () => {
                    try {
                      await pinEntityToDesktop({
                        userId: user.uid,
                        type: 'feature',
                        entityId: viewOnlyRequest.id,
                        title: viewOnlyRequest.title,
                        icon: '✨'
                      });
                      alert('Pinned to desktop');
                    } catch (err) {
                      alert(`Could not pin: ${err?.message || 'unknown error'}`);
                    }
                  }}
                  title="Pin this feature request to your desktop"
                >
                  📎 Pin to Desktop
                </button>
                <span className="view-only-badge">👁️ View Only</span>
                <button className="close-btn" onClick={handleCloseViewOnly}>×</button>
              </div>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>Title</label>
                <input type="text" value={viewOnlyRequest.title} disabled />
              </div>
              <div className="form-group">
                <label>Application</label>
                <input type="text" value={viewOnlyRequest.applicationName} disabled />
              </div>
              <div className="form-group">
                <label>Description</label>
                <textarea value={viewOnlyRequest.description || ''} disabled rows="4" />
              </div>
              {viewOnlyRequest.mockupImageUrl && (
                <div className="form-group">
                  <label>Mockup Image</label>
                  <div className="mockup-preview">
                    <img src={viewOnlyRequest.mockupImageUrl} alt="Feature Mockup" />
                  </div>
                </div>
              )}
              {viewOnlyRequest.testStep && (
                <div className="form-group">
                  <label>Test Step</label>
                  <textarea value={viewOnlyRequest.testStep} disabled rows="3" />
                </div>
              )}
              <div className="form-row">
                <div className="form-group">
                  <label>Priority</label>
                  <input type="text" value={viewOnlyRequest.priority} disabled />
                </div>
                <div className="form-group">
                  <label>Status</label>
                  <input type="text" value={viewOnlyRequest.status} disabled />
                </div>
              </div>
              <div className="form-group">
                <label>Net Votes</label>
                <input type="text" value={viewOnlyRequest.voteCount || 0} disabled />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Assigned to Dev</label>
                  <input type="text" value={viewOnlyRequest.assignedToDev || 'Unassigned'} disabled />
                </div>
                <div className="form-group">
                  <label>Assigned to Test</label>
                  <input type="text" value={viewOnlyRequest.assignedToTest || 'Unassigned'} disabled />
                </div>
              </div>
              <div className="form-group">
                <label>Created By</label>
                <input type="text" value={viewOnlyRequest.createdByEmail || 'Unknown'} disabled />
              </div>
              <div className="form-group">
                <label>Comments ({viewOnlyRequest.comments?.length || 0})</label>
                <div className="comments-view-only">
                  {viewOnlyRequest.comments?.map((comment, idx) => {
                    // Handle different date formats safely
                    let dateStr = 'Unknown date';
                    if (comment.createdAt) {
                      try {
                        if (typeof comment.createdAt.toDate === 'function') {
                          // Firestore Timestamp
                          dateStr = comment.createdAt.toDate().toLocaleString();
                        } else if (comment.createdAt instanceof Date) {
                          // JavaScript Date
                          dateStr = comment.createdAt.toLocaleString();
                        } else if (typeof comment.createdAt === 'string') {
                          // String date
                          dateStr = new Date(comment.createdAt).toLocaleString();
                        }
                      } catch (e) {
                        console.warn('Error formatting comment date:', e);
                      }
                    }
                    
                    return (
                      <div key={idx} className="comment-item-readonly">
                        <strong>{comment.email}</strong>
                        <span className="comment-date">{dateStr}</span>
                        <p>{comment.text}</p>
                      </div>
                    );
                  })}
                  {(!viewOnlyRequest.comments || viewOnlyRequest.comments.length === 0) && (
                    <p className="no-comments">No comments yet</p>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default FeatureRequestsApp;
